import { INITIATE_DOC_ON_CALL } from "graphql/profiles";
import { docOnCallType } from "graphql/types/globalTypes";
import {
  initiateDocOnCall,
  initiateDocOnCallVariables,
} from "graphql/types/initiateDocOnCall";
import { useAuth, useLoginPopupState } from "hooks/authHooks";
import dynamic from "next/dynamic";
import Link from "next/link";
import React, { useState, useEffect } from "react";
import { useApolloClient } from "react-apollo-hooks";
import { eventTracking } from "clevertapTracking";
import { LazyIntersection } from "../lib/LazyIntersection";
import WarningModel from "../WarningModel";
import { ICONS_PATHS } from "helpers/constants";
import { GenerateIcon } from "helpers/iconGenerator";
import {
  getPatientAttributes,
  getPatientDetails,
  HomePageEvents,
} from "../../helpers/commonHelpers";
const iconPathsVaccineFaq = ICONS_PATHS.slice(0, 8);
const iconPathsVaccineCall = ICONS_PATHS.slice(0, 3);
const iconPathsVaccineChat = ICONS_PATHS.slice(0, 6);
const DynamicCallDoctorModal = dynamic(
  () =>
    import("../VaccinationQueriesModal").then(
      (defaultComponent) => defaultComponent.default
    ),
  { ssr: false }
);
import styles from "./VaccinationSection.module.scss";
import { dataLayerTracking } from "gtmTracking";

const VaccinationSection: React.FC = (props) => {
  const [callDoctorPopup, setCallDoctorPopup] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = React.useState<object>();
  const { isSignedIn, currentPatient } = useAuth();
  const { setIsLoginPopupVisible } = useLoginPopupState();
  const patientDetails = getPatientDetails(currentPatient);
  const [exotelCallPrompt, setExotelCallPrompt] = useState<{
    DCOTOR_CONNECTING: string;
    CONNECT_ERROR: string;
  }>(null);
  const client = useApolloClient();

  useEffect(() => {
    const loadConstants = async () => {
      const constants = await import("helpers/constants").then(
        (client) => client.EXOTEL_CALL_PROMPT
      );
      setExotelCallPrompt(constants);
    };
    loadConstants();
  }, []);

  const initiateExotelCall = (mobileNumber: string) => {
    client
      .query<initiateDocOnCall, initiateDocOnCallVariables>({
        query: INITIATE_DOC_ON_CALL,
        variables: {
          mobileNumber,
          callType: docOnCallType.COVID_VACCINATION_QUERY,
        },
        fetchPolicy: "no-cache",
      })
      .then((response) => {
        response.data.initiateDocOnCall.success
          ? setSuccessMessage({
              message: exotelCallPrompt.DCOTOR_CONNECTING,
            })
          : setSuccessMessage({
              message: exotelCallPrompt.CONNECT_ERROR,
            });
        setCallDoctorPopup(false);
      })
      .catch((error) => {
        console.log(error);
        setCallDoctorPopup(false);
        setSuccessMessage({
          message: exotelCallPrompt.CONNECT_ERROR,
        });
      });
  };

  return (
    <div className={styles.vaccinationContainer}>
      <div className={styles.helpCard}>
        <div className={styles.helpSection}>
          <div className={styles.helpSectionHead}>
            For COVID-19 Vaccination related queries
          </div>
          <div className={styles.serviceContent}>
            <Link href={"blog/covid19-vaccines"} prefetch={false}>
              <div
                className={styles.serviceCard}
                onClick={() => {
                  dataLayerTracking({
                    event: HomePageEvents.event,
                    clicked_section: HomePageEvents.covid_queries,
                    clicked_item_name: HomePageEvents.faq_articles,
                  });
                  eventTracking("Vaccination FAQs & Articles clicked", {
                    ...getPatientAttributes(patientDetails),
                    Nav_src: "Vaccination widget",
                  });
                }}
              >
                <a className={styles.mainBx}>
                  <span className="iconMain">
                    <GenerateIcon
                      pathName={iconPathsVaccineFaq}
                      iconClass={"icon-vaccine_faq"}
                    />
                  </span>
                  <span>FAQs &amp; Articles</span>
                </a>
              </div>
            </Link>

            {/* <div
              className={styles.serviceCard}
              onClick={() => {
                if (isSignedIn) {
                  setCallDoctorPopup(true);
                  dataLayerTracking({
                    event: "popup_appeared",
                    popup_name: "Vaccination Queries",
                  });
                  dataLayerTracking({
                    event: HomePageEvents.event,
                    clicked_section: HomePageEvents.covid_queries,
                    clicked_item_name: HomePageEvents.vaccination_queries,
                  });
                  eventTracking("Vaccination Call a doctor clicked", {
                    ...getPatientAttributes(patientDetails),
                    Nav_src: "Vaccination widget",
                  });
                } else {
                  setIsLoginPopupVisible(true);
                }
              }}
            >
              <a className={styles.mainBx}>
                <span className="iconMain">
                  <GenerateIcon
                    pathName={iconPathsVaccineCall}
                    iconClass={"icon-vaccine_call"}
                  />
                </span>
                <span>Vaccination Queries</span>
              </a>
            </div> */}

            <Link
              href={process.env.NEXT_PUBLIC_COVID_CHAT_BOT_URL}
              prefetch={false}
            >
              <a target="_blank">
                <div
                  className={styles.serviceCard}
                  onClick={() => {
                    dataLayerTracking({
                      event: HomePageEvents.event,
                      clicked_section: HomePageEvents.covid_queries,
                      clicked_item_name: HomePageEvents.chat_with_us,
                    });
                    eventTracking("Vaccination Chat with us", {
                      ...getPatientAttributes(patientDetails),
                      Nav_src: "Vaccination widget",
                    });
                  }}
                >
                  <span className="icon-vaccine_chat">
                    {iconPathsVaccineChat.map((item, index) => {
                      return <span key={index} className={item} />;
                    })}
                  </span>
                  <span>Chat with us</span>
                </div>
              </a>
            </Link>

            <Link href={process.env.NEXT_PUBLIC_COWIN_REG_URL} prefetch={false}>
              <div
                className={styles.serviceCard}
                onClick={() => {
                  dataLayerTracking({
                    event: HomePageEvents.event,
                    clicked_section: HomePageEvents.covid_queries,
                    clicked_item_name: HomePageEvents.cowin_registration,
                  });
                  eventTracking("Vaccine tracker on home page", {
                    ...getPatientAttributes(patientDetails),
                    Nav_src: "Vaccination widget",
                  });
                }}
              >
                <a className={styles.mainBx}>
                  <span>
                    <LazyIntersection
                      src={`${process.env.NEXT_PUBLIC_247_CDN_URL}/images/vaccine.png`}
                      alt="Vaccine"
                      fallbackImage={`${process.env.NEXT_PUBLIC_247_CDN_URL}/images/placeholder.svg`}
                    />
                  </span>
                  <span>Cowin Registration</span>
                </a>
              </div>
            </Link>
          </div>
        </div>
      </div>
      <DynamicCallDoctorModal
        initiateExotelCall={initiateExotelCall}
        callDoctorPopup={callDoctorPopup}
        setCallDoctorPopup={setCallDoctorPopup}
      />
      <WarningModel
        error={successMessage}
        onClose={() => {
          setSuccessMessage(null);
        }}
      />
    </div>
  );
};

export default VaccinationSection;
